import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, CheckCircle, XCircle, Clock, Target, TrendingUp, Award, 
  BookOpen, Zap, Users, ArrowRight, Play, RotateCcw, Lightbulb,
  ChevronRight, Timer, BarChart3, Flame, Star, MessageSquare, LogIn, LogOut, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AICoachChat from '@/components/AICoachChat';
import AuthModal from '@/components/AuthModal';
import CertPaywallModal from '@/components/CertPaywallModal';
import { useAuth } from '@/context/AuthContext';
import { useCertAccess } from '@/lib/certAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Practice Modes for AI-102
const PRACTICE_MODES = [
  { id: 'quick', name: 'Quick Practice', icon: Zap, desc: '10 random questions', questions: 10, color: 'bg-blue-500' },
  { id: 'topic', name: 'Topic Mode', icon: BookOpen, desc: 'Focus on specific skill area', questions: 10, color: 'bg-green-500' },
  { id: 'simulation', name: 'Exam Simulation', icon: Timer, desc: '50 questions, 120 min timer', questions: 50, color: 'bg-purple-500' },
  { id: 'weak', name: 'Weak Areas', icon: Target, desc: 'Practice your weak spots', questions: 10, color: 'bg-orange-500' },
  { id: 'flashcards', name: 'Flashcards', icon: RotateCcw, desc: 'Review key concepts', questions: 0, color: 'bg-pink-500' },
];

export default function AI102ExamPrepPage() {
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userId] = useState(() => {
    const storedId = localStorage.getItem('ai102_user_id');
    return storedId || `user_${Date.now()}`;
  });
  const effectiveUserId = user?.id || userId;
  const proEmail = user?.email || localStorage.getItem('paywall_email') || '';
  const { unlocked: proUnlocked, refresh: refreshAccess } = useCertAccess('ai-102', proEmail);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('Exam Simulation');

  const requirePro = (feature, cb) => {
    if (proUnlocked) return cb();
    setPaywallFeature(feature);
    setPaywallOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id')) refreshAccess();
  }, [refreshAccess]);
  
  // AI-102 specific exam info
  const examInfo = {
    name: "AI-102",
    title: "Azure AI Engineer Associate",
    questions: 50,
    duration: 120,
    passingScore: 70,
    cost: "$165 USD"
  };
  const [progress, setProgress] = useState(null);
  const [skillAreas, setSkillAreas] = useState({});
  const [platformStats, setPlatformStats] = useState(null);
  const [examTips, setExamTips] = useState(null);
  
  // Practice state
  const [practiceMode, setPracticeMode] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [practiceStats, setPracticeStats] = useState({ correct: 0, total: 0 });
  
  // Simulation state
  const [simulationActive, setSimulationActive] = useState(false);
  const [simulationTime, setSimulationTime] = useState(3600);
  const [simulationAnswers, setSimulationAnswers] = useState({});
  const [simulationResult, setSimulationResult] = useState(null);
  
  // Flashcard state
  const [flashcards, setFlashcards] = useState([]);
  const [currentFlashcard, setCurrentFlashcard] = useState(0);
  const [showFlashcardBack, setShowFlashcardBack] = useState(false);

  // Initialize user
  useEffect(() => {
    localStorage.setItem('ai102_user_id', effectiveUserId);
    fetchProgress();
    fetchSkillAreas();
    fetchPlatformStats();
    fetchExamTips();
  }, [effectiveUserId]);

  // Helper to open auth modal
  const openAuth = (tab = 'login') => {
    setAuthModalTab(tab);
    setShowAuthModal(true);
  };

  // Simulation timer
  useEffect(() => {
    let interval;
    if (simulationActive && simulationTime > 0) {
      interval = setInterval(() => {
        setSimulationTime(prev => prev - 1);
      }, 1000);
    } else if (simulationTime === 0 && simulationActive) {
      submitSimulation();
    }
    return () => clearInterval(interval);
  }, [simulationActive, simulationTime]);

  const fetchProgress = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai102/progress/${effectiveUserId}`);
      const data = await res.json();
      setProgress(data);
    } catch (error) {
      console.error('Failed to fetch progress');
    }
  };

  const fetchSkillAreas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai102/skill-areas`);
      const data = await res.json();
      setSkillAreas(data);
    } catch (error) {
      console.error('Failed to fetch skill areas');
    }
  };

  const fetchPlatformStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai102/stats`);
      const data = await res.json();
      setPlatformStats(data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchExamTips = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai102/exam-tips`);
      const data = await res.json();
      setExamTips(data);
    } catch (error) {
      console.error('Failed to fetch tips');
    }
  };

  const startPractice = async (mode, skillArea = null) => {
    if (mode.id === 'weak' && !proUnlocked) {
      setPaywallFeature('Weak-Areas Practice');
      setPaywallOpen(true);
      return;
    }
    setPracticeMode(mode);
    setPracticeStats({ correct: 0, total: 0 });
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    
    try {
      let url = `${API_URL}/api/ai102/questions?limit=${mode.questions}&mode=practice`;
      if (skillArea) url += `&skill_area=${skillArea}`;
      if (mode.id === 'weak') url = `${API_URL}/api/ai102/weak-areas/${effectiveUserId}?limit=10`;
      
      const res = await fetch(url);
      const data = await res.json();
      setQuestions(data.questions || []);
      setActiveTab('practice');
    } catch (error) {
      toast.error('Failed to load questions');
    }
  };

  const startSimulation = async () => {
    if (!proUnlocked) {
      setPaywallFeature('Exam Simulation');
      setPaywallOpen(true);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/ai102/simulation/start?user_id=${effectiveUserId}`, { method: 'POST' });
      const data = await res.json();
      setQuestions(data.questions);
      setSimulationTime(data.time_limit);
      setSimulationAnswers({});
      setSimulationActive(true);
      setSimulationResult(null);
      setActiveTab('simulation');
    } catch (error) {
      toast.error('Failed to start simulation');
    }
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null) return;
    
    const question = questions[currentQuestionIndex];
    try {
      const res = await fetch(`${API_URL}/api/ai102/submit-answer?question_id=${question.id}&selected_answer=${selectedAnswer}&user_id=${effectiveUserId}&mode=${practiceMode?.id || 'practice'}`, {
        method: 'POST'
      });
      const data = await res.json();
      setResult(data);
      setShowResult(true);
      setPracticeStats(prev => ({
        correct: prev.correct + (data.is_correct ? 1 : 0),
        total: prev.total + 1
      }));
      fetchProgress();
    } catch (error) {
      toast.error('Failed to submit answer');
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setResult(null);
    } else {
      // Practice complete
      toast.success(`Practice complete! Score: ${practiceStats.correct + (result?.is_correct ? 1 : 0)}/${practiceStats.total + 1}`);
      setActiveTab('dashboard');
      setPracticeMode(null);
    }
  };

  const submitSimulation = async () => {
    setSimulationActive(false);
    const timeTaken = 3600 - simulationTime;
    
    try {
      const res = await fetch(`${API_URL}/api/ai102/simulation/submit?simulation_id=sim&time_taken=${timeTaken}&user_id=${effectiveUserId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: simulationAnswers, time_taken: timeTaken })
      });
      const data = await res.json();
      setSimulationResult(data);
      fetchProgress();
    } catch (error) {
      toast.error('Failed to submit simulation');
    }
  };

  const loadFlashcards = async (skillArea = null) => {
    try {
      let url = `${API_URL}/api/ai102/flashcards`;
      if (skillArea) url += `?skill_area=${skillArea}`;
      const res = await fetch(url);
      const data = await res.json();
      setFlashcards(data.flashcards);
      setCurrentFlashcard(0);
      setShowFlashcardBack(false);
      setActiveTab('flashcards');
    } catch (error) {
      toast.error('Failed to load flashcards');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen pt-24 bg-slate-50" data-testid="ai102-exam-prep">
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        defaultTab={authModalTab}
        certName="AI-102"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-violet-600 to-purple-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* User Status Bar */}
          <div className="flex justify-end mb-4">
            {isAuthenticated ? (
              <div className="flex items-center gap-3 bg-white/10 rounded-full px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <Badge className="bg-emerald-500/80 text-white text-xs">
                    {user?.plan === 'free' ? 'Free' : user?.plan === 'pro' ? 'Pro' : 'Team'}
                  </Badge>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-white/80 hover:text-white hover:bg-white/10"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-1" /> Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10"
                  onClick={() => openAuth('login')}
                  data-testid="login-btn"
                >
                  <LogIn className="w-4 h-4 mr-1" /> Sign In
                </Button>
                <Button 
                  size="sm"
                  className="bg-white text-violet-700 hover:bg-violet-50"
                  onClick={() => openAuth('register')}
                  data-testid="signup-btn"
                >
                  Sign Up Free
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <Badge className="bg-white/20 text-white mb-4">AI-Powered Exam Coach</Badge>
              <h1 className="text-3xl sm:text-4xl font-bold mb-4">
                Pass AI-102 Faster with Adaptive Learning
              </h1>
              <p className="text-violet-100 max-w-xl mb-6">
                Our AI coach identifies your weak areas and focuses your study time where it matters. 
                Join {platformStats?.total_learners?.toLocaleString() || '15,000'}+ learners with a {platformStats?.pass_rate || 89}% pass rate.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={startSimulation} 
                  className="bg-white text-violet-700 hover:bg-violet-50 rounded-full px-6"
                  data-testid="start-simulation-btn"
                >
                  <Play className="w-4 h-4 mr-2" /> Start Exam Simulation
                </Button>
                <Button 
                  variant="outline" 
                  className="border-white/50 text-white hover:bg-white/10 rounded-full px-6"
                  onClick={() => setActiveTab('modes')}
                >
                  Practice Modes
                </Button>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 w-full lg:w-auto">
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{progress?.readiness_score || 0}%</p>
                  <p className="text-sm text-violet-200">Readiness Score</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{progress?.study_streak || 0}</p>
                  <p className="text-sm text-violet-200">Day Streak 🔥</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{progress?.questions_answered || 0}</p>
                  <p className="text-sm text-violet-200">Questions Done</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{progress?.completed_simulations || 0}</p>
                  <p className="text-sm text-violet-200">Simulations</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-white shadow-sm">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="modes">Practice Modes</TabsTrigger>
            <TabsTrigger value="skills">Skill Areas</TabsTrigger>
            <TabsTrigger value="tips">Exam Tips</TabsTrigger>
            {practiceMode && <TabsTrigger value="practice">Practice</TabsTrigger>}
            {simulationActive && <TabsTrigger value="simulation">Simulation</TabsTrigger>}
            {flashcards.length > 0 && <TabsTrigger value="flashcards">Flashcards</TabsTrigger>}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Progress Overview */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart3 className="w-5 h-5 mr-2 text-violet-600" />
                      Your Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-600">Overall Readiness</span>
                        <span className="font-bold text-violet-600">{progress?.readiness_score || 0}%</span>
                      </div>
                      <Progress value={progress?.readiness_score || 0} className="h-3" />
                      <p className="text-xs text-slate-500 mt-1">70% required to pass the exam</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(skillAreas).map(([key, skill]) => {
                        const scores = progress?.skill_scores?.[key] || { correct: 0, total: 0 };
                        const pct = scores.total > 0 ? Math.round(scores.correct / scores.total * 100) : 0;
                        const isWeak = progress?.weak_areas?.includes(key);
                        return (
                          <div key={key} className={`p-3 rounded-lg ${isWeak ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-slate-700">{skill.name}</span>
                              {isWeak && <Badge variant="destructive" className="text-xs">Weak</Badge>}
                            </div>
                            <div className="flex items-center justify-between">
                              <Progress value={pct} className="flex-1 mr-2 h-2" />
                              <span className="text-sm font-bold">{pct}%</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{scores.correct}/{scores.total} correct</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="grid sm:grid-cols-3 gap-4">
                    <Button 
                      onClick={() => startPractice(PRACTICE_MODES[0])} 
                      className="h-auto py-4 flex flex-col items-center bg-blue-500 hover:bg-blue-600"
                    >
                      <Zap className="w-6 h-6 mb-2" />
                      <span>Quick Practice</span>
                      <span className="text-xs opacity-75">10 questions</span>
                    </Button>
                    <Button 
                      onClick={startSimulation}
                      className="h-auto py-4 flex flex-col items-center bg-purple-500 hover:bg-purple-600"
                    >
                      <Timer className="w-6 h-6 mb-2" />
                      <span>Exam Simulation</span>
                      <span className="text-xs opacity-75">60 minutes</span>
                    </Button>
                    <Button 
                      onClick={() => startPractice(PRACTICE_MODES[3])}
                      className="h-auto py-4 flex flex-col items-center bg-orange-500 hover:bg-orange-600"
                    >
                      <Target className="w-6 h-6 mb-2" />
                      <span>Weak Areas</span>
                      <span className="text-xs opacity-75">Focus practice</span>
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Exam Info */}
                <Card className="bg-violet-50 border-violet-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-violet-800">AI-102 Exam Info</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Questions:</span>
                      <span className="font-medium">32-40</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Duration:</span>
                      <span className="font-medium">60 minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Passing Score:</span>
                      <span className="font-medium text-green-600">700/1000 (70%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Exam Cost:</span>
                      <span className="font-medium">$99 USD</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Pass Guarantee */}
                <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                  <CardContent className="p-6 text-center">
                    <Award className="w-12 h-12 mx-auto mb-3 opacity-90" />
                    <h3 className="font-bold text-lg mb-2">Pass Guarantee</h3>
                    <p className="text-sm text-green-100 mb-4">
                      Pass the AI-102 exam or get a full refund*
                    </p>
                    <p className="text-xs text-green-200">
                      *Requires 80%+ questions completed & 75%+ practice score
                    </p>
                  </CardContent>
                </Card>

                {/* Trust Signals */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-slate-600">
                        <Users className="w-4 h-4 mr-1" />
                        {platformStats?.total_learners?.toLocaleString() || '15,000'}+ learners
                      </div>
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {platformStats?.pass_rate || 89}% pass rate
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Practice Modes Tab */}
          <TabsContent value="modes">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PRACTICE_MODES.map((mode) => (
                <Card key={mode.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => {
                  if (mode.id === 'simulation') requirePro('Exam Simulation', startSimulation);
                  else if (mode.id === 'weak') requirePro('Weak-Areas Practice', () => startPractice(mode));
                  else if (mode.id === 'flashcards') loadFlashcards();
                  else if (mode.id === 'topic') setActiveTab('skills');
                  else startPractice(mode);
                }}>
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${mode.color} rounded-xl flex items-center justify-center mb-4`}>
                      <mode.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900 mb-2">{mode.name}</h3>
                    <p className="text-slate-600 text-sm mb-4">{mode.desc}</p>
                    <Button className="w-full" variant="outline">
                      Start <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Skill Areas Tab */}
          <TabsContent value="skills">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(skillAreas).map(([key, skill]) => {
                const scores = progress?.skill_scores?.[key] || { correct: 0, total: 0 };
                const pct = scores.total > 0 ? Math.round(scores.correct / scores.total * 100) : 0;
                return (
                  <Card key={key} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Badge variant="outline">{skill.weight}% of exam</Badge>
                        <span className="text-lg font-bold text-violet-600">{pct}%</span>
                      </div>
                      <h3 className="font-bold text-lg text-slate-900 mb-2">{skill.name}</h3>
                      <Progress value={pct} className="mb-4" />
                      <p className="text-sm text-slate-500 mb-4">{scores.correct}/{scores.total} questions answered</p>
                      <Button 
                        className="w-full" 
                        onClick={() => startPractice(PRACTICE_MODES[1], key)}
                      >
                        Practice This Topic
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Exam Tips Tab */}
          <TabsContent value="tips">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Lightbulb className="w-5 h-5 mr-2 text-amber-500" />
                    Exam Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {examTips?.tips?.slice(0, 8).map((tip, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Star className="w-5 h-5 mr-2 text-violet-500" />
                    High-Frequency Topics
                  </CardTitle>
                  <CardDescription>These topics appear most often on the exam</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {examTips?.high_frequency_topics?.map((topic, i) => (
                      <li key={i} className="p-3 bg-violet-50 rounded-lg">
                        <Badge variant="outline" className="mb-1">{skillAreas[topic.skill]?.name}</Badge>
                        <p className="text-sm text-slate-700">{topic.question}...</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Practice Tab */}
          <TabsContent value="practice">
            {questions.length > 0 && currentQuestionIndex < questions.length && (
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <Badge variant="outline">Question {currentQuestionIndex + 1} of {questions.length}</Badge>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-500">
                      Score: {practiceStats.correct}/{practiceStats.total}
                    </span>
                    <Badge className={skillAreas[questions[currentQuestionIndex].skill_area]?.name ? 'bg-violet-100 text-violet-700' : ''}>
                      {skillAreas[questions[currentQuestionIndex].skill_area]?.name || questions[currentQuestionIndex].skill_area}
                    </Badge>
                  </div>
                </div>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-medium text-slate-900 mb-6">
                      {questions[currentQuestionIndex].question}
                    </h3>

                    <RadioGroup 
                      value={selectedAnswer?.toString()} 
                      onValueChange={(v) => !showResult && setSelectedAnswer(parseInt(v))}
                      className="space-y-3"
                    >
                      {questions[currentQuestionIndex].options.map((option, idx) => {
                        let bgClass = 'bg-slate-50 hover:bg-slate-100';
                        if (showResult) {
                          if (idx === result?.correct_answer) bgClass = 'bg-green-50 border-green-500';
                          else if (idx === selectedAnswer && !result?.is_correct) bgClass = 'bg-red-50 border-red-500';
                        }
                        return (
                          <div key={idx} className={`flex items-center space-x-3 p-4 rounded-lg border ${bgClass} transition-colors`}>
                            <RadioGroupItem value={idx.toString()} id={`option-${idx}`} disabled={showResult} />
                            <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                              {option}
                            </Label>
                            {showResult && idx === result?.correct_answer && (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            )}
                            {showResult && idx === selectedAnswer && !result?.is_correct && (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                        );
                      })}
                    </RadioGroup>

                    {showResult && result && (
                      <div className={`mt-6 p-4 rounded-lg ${result.is_correct ? 'bg-green-50' : 'bg-amber-50'}`}>
                        <h4 className={`font-bold mb-2 ${result.is_correct ? 'text-green-700' : 'text-amber-700'}`}>
                          {result.is_correct ? '✓ Correct!' : '✗ Incorrect'}
                        </h4>
                        <p className="text-slate-700 mb-3">{result.explanation}</p>
                        {result.exam_tip && (
                          <div className="flex items-start p-3 bg-white rounded border border-violet-200">
                            <Lightbulb className="w-4 h-4 text-violet-500 mr-2 mt-0.5" />
                            <p className="text-sm text-violet-700"><strong>Exam Tip:</strong> {result.exam_tip}</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between mt-6">
                      <Button 
                        variant="outline" 
                        onClick={() => { setActiveTab('dashboard'); setPracticeMode(null); }}
                      >
                        Exit Practice
                      </Button>
                      {!showResult ? (
                        <Button onClick={submitAnswer} disabled={selectedAnswer === null}>
                          Submit Answer
                        </Button>
                      ) : (
                        <Button onClick={nextQuestion}>
                          {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish'}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Simulation Tab */}
          <TabsContent value="simulation">
            {simulationActive && questions.length > 0 && (
              <div className="max-w-4xl mx-auto">
                {/* Timer Bar */}
                <div className="sticky top-20 z-10 bg-white shadow-sm p-4 rounded-lg mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-lg px-4 py-2">
                      <Clock className="w-4 h-4 mr-2" />
                      {formatTime(simulationTime)}
                    </Badge>
                    <span className="text-slate-600">
                      {Object.keys(simulationAnswers).length} of {questions.length} answered
                    </span>
                  </div>
                  <Button onClick={submitSimulation} variant="destructive">
                    Submit Exam
                  </Button>
                </div>

                {/* Questions Grid */}
                <div className="grid gap-4">
                  {questions.map((q, idx) => (
                    <Card key={q.id} className={simulationAnswers[q.id] !== undefined ? 'border-green-300' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <span className="text-sm font-bold text-violet-600 mt-1">{idx + 1}.</span>
                          <div className="flex-1">
                            <p className="text-slate-900 mb-3">{q.question}</p>
                            <RadioGroup 
                              value={simulationAnswers[q.id]?.toString()} 
                              onValueChange={(v) => setSimulationAnswers(prev => ({ ...prev, [q.id]: parseInt(v) }))}
                              className="space-y-2"
                            >
                              {q.options.map((option, optIdx) => (
                                <div key={optIdx} className="flex items-center space-x-2">
                                  <RadioGroupItem value={optIdx.toString()} id={`sim-${q.id}-${optIdx}`} />
                                  <Label htmlFor={`sim-${q.id}-${optIdx}`} className="text-sm">{option}</Label>
                                </div>
                              ))}
                            </RadioGroup>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Simulation Results */}
            {simulationResult && (
              <div className="max-w-2xl mx-auto">
                <Card className={simulationResult.passed ? 'border-green-500 border-2' : 'border-red-500 border-2'}>
                  <CardContent className="p-8 text-center">
                    <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${simulationResult.passed ? 'bg-green-100' : 'bg-red-100'}`}>
                      {simulationResult.passed ? (
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      ) : (
                        <XCircle className="w-10 h-10 text-red-600" />
                      )}
                    </div>
                    <h2 className="text-2xl font-bold mb-2">
                      {simulationResult.passed ? 'Congratulations! You Passed!' : 'Keep Practicing!'}
                    </h2>
                    <p className="text-4xl font-bold text-violet-600 mb-4">{simulationResult.score}%</p>
                    <p className="text-slate-600 mb-6">
                      {simulationResult.correct} correct out of {simulationResult.total} questions
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6 text-left">
                      {Object.entries(simulationResult.skill_breakdown || {}).map(([skill, scores]) => (
                        <div key={skill} className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">{skillAreas[skill]?.name || skill}</p>
                          <p className="font-bold">{scores.correct}/{scores.total}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-4 justify-center">
                      <Button onClick={() => setActiveTab('dashboard')}>
                        Back to Dashboard
                      </Button>
                      <Button onClick={startSimulation} variant="outline">
                        Try Again
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Flashcards Tab */}
          <TabsContent value="flashcards">
            {flashcards.length > 0 && (
              <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <Badge variant="outline">Card {currentFlashcard + 1} of {flashcards.length}</Badge>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('dashboard')}>
                    Exit Flashcards
                  </Button>
                </div>

                <Card 
                  className="min-h-[300px] cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setShowFlashcardBack(!showFlashcardBack)}
                >
                  <CardContent className="p-8 flex flex-col items-center justify-center min-h-[300px]">
                    {!showFlashcardBack ? (
                      <>
                        <Badge className="mb-4">{skillAreas[flashcards[currentFlashcard].skill_area]?.name}</Badge>
                        <p className="text-lg text-center text-slate-900">{flashcards[currentFlashcard].front}</p>
                        <p className="text-sm text-slate-400 mt-4">Click to reveal answer</p>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-8 h-8 text-green-500 mb-4" />
                        <p className="text-lg text-center text-slate-900 whitespace-pre-line">
                          {flashcards[currentFlashcard].back}
                        </p>
                        {flashcards[currentFlashcard].exam_tip && (
                          <div className="mt-4 p-3 bg-violet-50 rounded-lg w-full">
                            <p className="text-sm text-violet-700">
                              <Lightbulb className="w-4 h-4 inline mr-1" />
                              {flashcards[currentFlashcard].exam_tip}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                <div className="flex justify-between mt-6">
                  <Button 
                    variant="outline" 
                    onClick={() => { setCurrentFlashcard(Math.max(0, currentFlashcard - 1)); setShowFlashcardBack(false); }}
                    disabled={currentFlashcard === 0}
                  >
                    Previous
                  </Button>
                  <Button 
                    onClick={() => { setCurrentFlashcard(Math.min(flashcards.length - 1, currentFlashcard + 1)); setShowFlashcardBack(false); }}
                    disabled={currentFlashcard === flashcards.length - 1}
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Pricing Section */}
      <section className="py-16 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Choose Your Plan</h2>
            <p className="text-slate-600">Start free, upgrade when you're ready</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Free</CardTitle>
                <CardDescription>Get started</CardDescription>
                <p className="text-3xl font-bold">$0</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> 20 practice questions</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Basic progress tracking</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Skill area overview</li>
                </ul>
                <Button variant="outline" className="w-full mt-6">Current Plan</Button>
              </CardContent>
            </Card>

            <Card className="border-violet-500 border-2 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-violet-500">Most Popular</Badge>
              </div>
              <CardHeader>
                <CardTitle>Pro</CardTitle>
                <CardDescription>Full access</CardDescription>
                <p className="text-3xl font-bold">$39<span className="text-sm font-normal">/one-time</span></p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> 200+ practice questions</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Unlimited exam simulations</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> AI-powered explanations</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Weakness analysis</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Flashcards & study guides</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Pass guarantee</li>
                </ul>
                {proUnlocked ? (
                  <Button disabled className="w-full mt-6 bg-green-600 hover:bg-green-600 text-white" data-testid="pro-already-unlocked">
                    <CheckCircle className="w-4 h-4 mr-2" /> Pro — Unlocked
                  </Button>
                ) : (
                  <Button
                    className="w-full mt-6 bg-violet-600 hover:bg-violet-700"
                    onClick={() => { setPaywallFeature('Pro Plan'); setPaywallOpen(true); }}
                    data-testid="upgrade-to-pro-btn"
                  >
                    Upgrade to Pro
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Team</CardTitle>
                <CardDescription>For organizations</CardDescription>
                <p className="text-3xl font-bold">$29<span className="text-sm font-normal">/user</span></p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Everything in Pro</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Team dashboard</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Progress reports</li>
                  <li className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Bulk discounts</li>
                </ul>
                <Link to="/team-enrollment?cert=ai-102">
                  <Button variant="outline" className="w-full mt-6" data-testid="contact-sales-btn">Contact Sales</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Coach Chat - Floating component */}
      <AICoachChat 
        userId={effectiveUserId} 
        context={practiceMode ? `Currently in ${practiceMode.name} mode` : activeTab}
        locked={!proUnlocked}
        onLockedClick={() => { setPaywallFeature('AI Coach Chat'); setPaywallOpen(true); }}
      />

      {/* Per-cert paywall */}
      <CertPaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        certCode="ai-102"
        certName="AI-102: Azure AI Engineer Associate"
        amount={39}
        defaultEmail={proEmail}
        defaultName={user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''}
        feature={paywallFeature}
      />
    </div>
  );
}
